import { useEffect, useRef, useState } from 'react';
import {
  AgxCheckbox,
  AgxColumn,
  AgxCurrency,
  AgxTextInput,
  DisplayPriceModel,
  isRuralProperty,
  SaleType,
} from '@urbanx/agx-ui-components';
import './DisplayPrice.scss';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import { useAppSelector } from 'hooks/useAppSelector';

const toBeConfirmedLabel = 'To be confirmed';

interface DisplayPriceProps {
  id: string;
  label: string;
  subLabel?: string | null;
  nonRuralAgreementSubLabel?: string | null;
  subDescription?: string | null;
  onValueChanged: (value: { id: string; value: DisplayPriceModel | null }) => void;
  defaultValue: DisplayPriceModel | null;
  showToBeConfirmed: boolean | null;
  showReservePrice: boolean | null;
  validate: boolean;
  required?: boolean;
}

const DisplayPrice = ({
  id,
  label,
  subLabel,
  nonRuralAgreementSubLabel,
  subDescription,
  onValueChanged,
  defaultValue = null,
  showToBeConfirmed = false,
  showReservePrice = false,
  validate,
  required = false,
}: DisplayPriceProps) => {
  const propertyDetails = useFormPropertyDetailsSelector();

  const saleDetails = useAppSelector((state) => state.form?.formValues?.SaleDetails);

  const isRural = isRuralProperty(propertyDetails.landAreaDetails);

  const [toBeConfirmed, setToBeConfirmed] = useState(defaultValue?.toBeConfirmed ?? false);
  const [price, setPrice] = useState<string | null>(defaultValue?.price ?? null);
  const onValueChangedRef = useRef(onValueChanged);

  useEffect(() => {
    onValueChangedRef.current = onValueChanged;
  }, [onValueChanged]);

  useEffect(() => {
    setToBeConfirmed(defaultValue?.toBeConfirmed ?? false);
  }, [defaultValue?.toBeConfirmed]);

  useEffect(() => {
    setPrice(defaultValue?.price ?? null);
  }, [defaultValue?.price]);

  useEffect(() => {
    onValueChangedRef.current({
      id,
      value: {
        toBeConfirmed,
        price,
      },
    });
  }, [id, price, toBeConfirmed]);

  const onTextChanged = ({ value }: { value: string | null }) => {
    if (!toBeConfirmed) {
      setPrice(value);
    }
  };

  const onCheckboxChanged = ({ value }: { value: boolean }) => {
    setToBeConfirmed(value);
  };

  const hasError = validate && required && !price;
  const isAuction = saleDetails?.saleType === SaleType.Auction;
  const isPrivateTreaty = saleDetails?.saleType === SaleType.PrivateTreaty;
  const shouldUseCurrencyInput = isAuction && Boolean(showReservePrice);

  const inputLabel = shouldUseCurrencyInput
    ? 'Reserve Price'
    : !isRural && isPrivateTreaty
      ? 'Display Price'
      : label;
  const inputDefaultValue = toBeConfirmed ? toBeConfirmedLabel : (price ?? undefined);

  return (
    <AgxColumn fill largeGap>
      <AgxColumn fill mediumGap>
        {shouldUseCurrencyInput ? (
          <AgxCurrency
            key={toBeConfirmed ? 'toBeConfirmed-currency' : 'displayPrice-currency'}
            id={id}
            label="Reserve Price"
            onInputValueChange={onTextChanged}
            stretch
            disabled={toBeConfirmed}
            required={required}
            defaultValue={inputDefaultValue}
            subDescription={subDescription}
            validate={validate}
            noOptionalLabel
            displayErrors={hasError}
            error={hasError ? 'Enter reserve price' : undefined}
          />
        ) : (
          <AgxTextInput
            key={toBeConfirmed ? 'toBeConfirmed-text' : 'displayPrice-text'}
            extraClasses={subLabel ? 'extraGapForSubLabel' : undefined}
            id={id}
            label={inputLabel}
            onInputValueChange={onTextChanged}
            stretch
            disabled={toBeConfirmed}
            noOptionalLabel
            required={required}
            defaultValue={inputDefaultValue}
            subDescription={subDescription ?? undefined}
            subLabel={!isRural ? (nonRuralAgreementSubLabel ?? undefined) : (subLabel ?? undefined)}
            error={hasError ? `Enter ${inputLabel.toLowerCase()}` : ''}
          />
        )}
        {showToBeConfirmed && (
          <AgxCheckbox
            id={`${id}Chkbox`}
            onValueChanged={onCheckboxChanged}
            subLabel={`"${toBeConfirmedLabel}"`}
            stretch
            naked
            defaultValue={toBeConfirmed}
          />
        )}
      </AgxColumn>
    </AgxColumn>
  );
};

export default DisplayPrice;
